import React, { useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { Loader2, Plus, Sparkles, Tag as TagIcon, X } from 'lucide-react';
import mediaService from '@services/mediaService';
import type { MediaTag, MediaTagCategory } from '@/types/media';
import { chipFromTag, sameTag, type TagChip } from '@/utils/mediaTags';

export type { TagChip };

interface TagInputProps {
  value: TagChip[];
  onChange: (value: TagChip[]) => void;
  /** Permite criar tag nova por nome numa categoria escolhida (edição de mídia). */
  allowCreate?: boolean;
  /** Categorias já carregadas pela tela (senão o componente busca). */
  categories?: MediaTagCategory[];
  placeholder?: string;
  disabled?: boolean;
  /** Agrupa os chips por categoria (edição); no filtro fica tudo numa linha. */
  grouped?: boolean;
  className?: string;
}

/**
 * Seletor de tags com autocompletar (GET /media/tags?search=), chips agrupados por categoria e,
 * em `allowCreate`, criação de tag nova por nome numa categoria.
 */
export const TagInput: React.FC<TagInputProps> = ({
  value,
  onChange,
  allowCreate = false,
  categories: categoriesProp,
  placeholder = 'Buscar tag…',
  disabled = false,
  grouped = false,
  className,
}) => {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<MediaTag[]>([]);
  const [categories, setCategories] = useState<MediaTagCategory[]>(categoriesProp ?? []);
  const [createCategory, setCreateCategory] = useState('');
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (categoriesProp) {
      setCategories(categoriesProp);
      return;
    }
    if (!allowCreate) return;
    mediaService
      .tagCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, [categoriesProp, allowCreate]);

  const activeCategories = useMemo(
    () => categories.filter((c) => c.is_active).sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, 'pt-BR')),
    [categories],
  );

  useEffect(() => {
    if (!createCategory && activeCategories.length > 0) setCreateCategory(String(activeCategories[0].id));
  }, [activeCategories, createCategory]);

  // Busca com debounce enquanto a lista estiver aberta.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = window.setTimeout(async () => {
      setLoading(true);
      try {
        const list = await mediaService.tags({ search: text.trim() || undefined });
        if (!cancelled) {
          setResults(list.filter((tag) => tag.is_active));
          setHighlight(0);
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [text, open]);

  // Clique fora fecha.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const options = useMemo(
    () => results.filter((t) => !value.some((v) => sameTag(v, chipFromTag(t)))).slice(0, 30),
    [results, value],
  );

  const optionGroups = useMemo(() => {
    const groups = new Map<string, MediaTag[]>();
    options.forEach((t) => {
      const list = groups.get(t.category_name) ?? [];
      list.push(t);
      groups.set(t.category_name, list);
    });
    return [...groups.entries()];
  }, [options]);

  const trimmed = text.trim();
  const canCreate =
    allowCreate &&
    trimmed.length > 0 &&
    !!createCategory &&
    !results.some(
      (t) => t.category_id === Number(createCategory) && t.name.toLowerCase() === trimmed.toLowerCase(),
    );

  const add = (chip: TagChip) => {
    if (value.some((v) => sameTag(v, chip))) return;
    onChange([...value, chip]);
    setText('');
  };

  const remove = (chip: TagChip) => onChange(value.filter((v) => !sameTag(v, chip)));

  const createNew = () => {
    const cat = activeCategories.find((c) => c.id === Number(createCategory));
    if (!cat || !trimmed) return;
    add({ category_id: cat.id, category_name: cat.name, name: trimmed, source: 'user' });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, Math.max(options.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (options[highlight]) add(chipFromTag(options[highlight]));
      else if (canCreate) createNew();
    } else if (e.key === 'Backspace' && !text && value.length > 0) {
      remove(value[value.length - 1]);
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation();
      setOpen(false);
    }
  };

  const chip = (c: TagChip) => (
    <span
      key={`${c.tag_id ?? 'novo'}-${c.category_id}-${c.name}`}
      className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        c.tag_id ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200',
      )}
      title={c.tag_id ? `${c.category_name}: ${c.name}` : `Nova tag em ${c.category_name} (criada ao salvar)`}
    >
      {c.source === 'ai' && <Sparkles size={10} className="text-violet-500" />}
      {!grouped && <span className="text-emerald-500/80">{c.category_name}:</span>}
      {c.name}
      {!disabled && (
        <button
          type="button"
          onClick={() => remove(c)}
          className="ml-0.5 rounded-full hover:bg-black/10 p-0.5"
          aria-label={`Remover ${c.name}`}
        >
          <X size={10} />
        </button>
      )}
    </span>
  );

  const chipGroups = useMemo(() => {
    const groups = new Map<string, TagChip[]>();
    value.forEach((c) => {
      const list = groups.get(c.category_name) ?? [];
      list.push(c);
      groups.set(c.category_name, list);
    });
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'));
  }, [value]);

  let flatIndex = -1;

  return (
    <div ref={boxRef} className={clsx('relative', className)}>
      {grouped && value.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {chipGroups.map(([cat, chips]) => (
            <div key={cat} className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-semibold text-slate-500 w-24 truncate" title={cat}>
                {cat}
              </span>
              {chips.map(chip)}
            </div>
          ))}
        </div>
      )}
      <div
        className={clsx(
          'input-field flex flex-wrap items-center gap-1.5 min-h-[38px] py-1.5',
          disabled && 'bg-slate-50 cursor-not-allowed',
        )}
        onClick={() => !disabled && setOpen(true)}
      >
        <TagIcon size={14} className="text-slate-400" />
        {!grouped && value.map(chip)}
        {!disabled && (
          <input
            className="flex-1 min-w-[8rem] outline-none bg-transparent text-sm"
            placeholder={value.length === 0 || grouped ? placeholder : ''}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
          />
        )}
      </div>

      {open && !disabled && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-y-auto custom-scrollbar">
          {loading && results.length === 0 && (
            <p className="px-3 py-2 text-sm text-slate-400">
              <Loader2 size={14} className="inline animate-spin mr-1" /> Buscando…
            </p>
          )}
          {!loading && options.length === 0 && !canCreate && (
            <p className="px-3 py-2 text-sm text-slate-400">Nenhuma tag encontrada.</p>
          )}
          {optionGroups.map(([cat, tags]) => (
            <div key={cat}>
              <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{cat}</p>
              {tags.map((t) => {
                flatIndex++;
                const idx = flatIndex;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onMouseEnter={() => setHighlight(idx)}
                    onClick={() => add(chipFromTag(t))}
                    className={clsx(
                      'w-full text-left px-3 py-1.5 text-sm flex items-center justify-between gap-2',
                      idx === highlight ? 'bg-emerald-50 text-emerald-800' : 'text-slate-700 hover:bg-slate-50',
                    )}
                  >
                    <span>
                      {t.name}
                      {t.synonyms.length > 0 && (
                        <span className="text-xs text-slate-400 ml-2">({t.synonyms.join(', ')})</span>
                      )}
                    </span>
                    <span className="text-xs text-slate-400 tabular-nums">{t.usage_count}</span>
                  </button>
                );
              })}
            </div>
          ))}
          {allowCreate && trimmed && (
            <div className="border-t border-slate-100 px-3 py-2 flex flex-wrap items-center gap-2 bg-slate-50">
              <span className="text-xs text-slate-600">Criar “{trimmed}” em</span>
              <select
                className="select-field w-auto py-1 text-xs"
                value={createCategory}
                onChange={(e) => setCreateCategory(e.target.value)}
              >
                {activeCategories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={!canCreate}
                onClick={createNew}
                className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900 disabled:opacity-40"
              >
                <Plus size={12} /> Criar tag
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TagInput;
